'use client';

import { type FC, useMemo, useContext } from 'react';
import { Text, Timestamp, Flex, ButtonGroup, Button } from '@/_components';
import { getClasses } from '@/_utils/classnames';
import { NotificationsContext } from '@/_contexts/admin/NotificationsProvider';
import styles from './NotificationItem.module.css';
import { QuestionnaireContext } from '@/_contexts/admin/QuestionnaireProvider';
import { getQuestionnaire } from '@/_api/client-questionnaires';
import { Questionnaire } from '@/_types/models/questionnaire';
import { transformDocumentPath } from './NotificationItem.utils';
import { UserContext } from '@/_contexts/UserProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import type { NotificationItemProps } from './NotificationItem.types';
import { useRouter } from 'next/navigation';
import {
  useDownloadNotification,
  isDownloadLink,
} from '@/_utils/notifications';

export const NotificationItem: FC<NotificationItemProps> = ({
  notification,
  onClick,
}) => {
  const { setOpen } = useContext(NotificationsContext);
  const { setQuestionnairePreview, setQuestionnairePreviewOpen } =
    useContext(QuestionnaireContext);
  const { token } = useContext(UserContext);
  const { addToast } = useContext(ToastContext);
  const { push } = useRouter();

  const downloadNotification = useDownloadNotification();
  const isLinkDownloadable: boolean = isDownloadLink(notification?.link || '');

  const onDownload = async () => {
    if (!isLinkDownloadable) return;

    try {
      await downloadNotification(notification);
    } catch (error) {
      addToast('error', 'There was a problem downloading the document.', {
        error,
      });
    }
  };

  const notificationType: 'questionnaire' | 'document' | undefined =
    useMemo(() => {
      const isQuestionnaire: boolean =
        !!notification?.link?.includes('?companyId=') &&
        !!notification?.link?.includes('&questionnaireId=');

      if (isQuestionnaire) return 'questionnaire';

      const isDocumentUpload: boolean =
        !!notification?.link?.includes('/admin-documents');

      if (isDocumentUpload) return 'document';

      return undefined;
    }, [notification?.link]);

  if (!notification?.id) return;

  const onItemClick = async (event: React.PointerEvent<Element>) => {
    onClick?.(event);

    switch (notificationType) {
      case 'questionnaire': {
        const params = new URLSearchParams(notification?.link?.split('?')?.[1]);
        const companyId: string | null = params.get('companyId');
        const questionnaireId: string | null = params.get('questionnaireId');

        if (!companyId || !questionnaireId) {
          return;
        }

        const questionnaire: Questionnaire = await getQuestionnaire({
          companyId: parseInt(companyId),
          questionnaireId: parseInt(questionnaireId),
          token,
        });

        if (questionnaire) {
          setOpen(false);
          setQuestionnairePreview(questionnaire);
          setQuestionnairePreviewOpen(true);
        } else {
          addToast('error', 'Failed to load questionnaire');
        }

        return { success: true };
      }
      case 'document': {
        const documentPath = transformDocumentPath(notification?.link);

        setOpen(false);
        push(documentPath);

        return { success: true };
      }
      default: {
        if (!notification?.dismissed) {
          onDownload?.();
        }

        return { success: true };
      }
    }
  };

  const notificationItemClasses: string = getClasses(styles.notificationItem, {
    [styles.notificationItemUnread]: !notification?.dismissed,
  });

  return (
    <Flex
      gap={6}
      padding={8}
      className={notificationItemClasses}
      isBetween={true}
      isColumn={false}
      isYCentered={true}
    >
      <Flex gap={6} onClick={onItemClick}>
        <Flex isColumn={true}>
          <Text type="small">
            {notification?.client_name && (
              <span>
                <b>{notification?.client_name}</b>:
              </span>
            )}{' '}
            {notification?.text}
          </Text>
          <Text type="tiny" color="secondary">
            <Timestamp
              timestamp={notification?.created_at}
              isFromNow={true}
              hideTime={true}
            />
          </Text>
        </Flex>
      </Flex>
      {isLinkDownloadable ? (
        <ButtonGroup>
          <Button
            intent="secondary"
            onClick={onDownload}
            value="Download"
            withTooltip={false}
            size="tiny"
            iconLeft="download"
          />
        </ButtonGroup>
      ) : (
        <Button
          intent="ghost"
          size="tiny"
          iconLeft="x"
          isIconOnly={true}
          value="Dismiss"
          onClick={onClick}
        />
      )}
    </Flex>
  );
};
